import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useMemo } from 'react';

import type { LocationHistoryMinimal } from '~hooks/useLocationHistory/models';
import { useGetCycleSegments } from '~pages/LiveMap/Timeline/components/hooks/useGetCycleSegments';
import { useIndicatorValues } from '~pages/LiveMap/Timeline/components/hooks/useIndicatorValues';
import { useTimeNormalization } from '~pages/LiveMap/Timeline/components/hooks/useTimeNormalization';
import TimeIndicator from '~pages/LiveMap/Timeline/components/TimeIndicator';
import { useStores } from '~store';
import { clampSpeed, formatSpeed, formatSpeedRounded } from '~utils/speed';

import {
  getNoSignalBlocks,
  TimelineTickLabels,
  TimelineTickLines,
  TimelineTimeDisplay,
} from './timelineTicks';

const GRAPH_HEIGHT = 80;
const LABEL_WIDTH = 80;
const Y_AXIS_LABELS = 4;

/**
 * builds a smooth SVG path (catmull-rom → cubic bezier) from the speed data.
 * returns both the stroke path and the filled area path.
 */
export function buildSpeedPath(
  pings: LocationHistoryMinimal[],
  startMs: number,
  durationMs: number,
  maxSpeed: number,
  width: number,
  height: number,
): { strokePath: string; fillPath: string } {
  const points: { x: number; y: number }[] = [];

  for (const p of pings) {
    if (p.speed === null) continue;
    const x =
      durationMs > 0 ? ((p.occurredAt.valueOf() - startMs) / durationMs) * width : 0;
    const speed = clampSpeed(parseFloat(p.speed));
    const y = maxSpeed > 0 ? (1 - speed / maxSpeed) * height : height;
    points.push({ x, y });
  }

  if (points.length === 0) return { strokePath: '', fillPath: '' };
  if (points.length === 1) {
    const p = points[0];
    return {
      strokePath: `M${p.x},${p.y}`,
      fillPath: `M${p.x},${height}L${p.x},${p.y}L${p.x},${height}Z`,
    };
  }

  // catmull-rom to cubic bezier conversion
  const bezierSegments: string[] = [];
  bezierSegments.push(`M${points[0].x},${points[0].y}`);

  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(i - 1, 0)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(i + 2, points.length - 1)];

    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;

    bezierSegments.push(`C${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`);
  }

  const strokePath = bezierSegments.join('');
  const fillPath = `${strokePath}L${points[points.length - 1].x},${height}L${points[0].x},${height}Z`;

  return { strokePath, fillPath };
}

interface Props {
  currentPing: LocationHistoryMinimal | null;
  pings: LocationHistoryMinimal[];
}

export const DistanceSpeedTimeline = ({ currentPing, pings }: Props) => {
  const theme = useTheme();
  const { userStore } = useStores();
  const isMetric = userStore?.userCompany.isMeters;

  const firstPingTime = pings[0].occurredAt;
  const lastPingTime = pings[pings.length - 1].occurredAt;
  const startMs = firstPingTime.valueOf();
  const endMs = lastPingTime.valueOf();
  const durationMs = endMs - startMs;
  const minTime = firstPingTime.toISOString();
  const maxTime = lastPingTime.toISOString();

  const normalize = useTimeNormalization(minTime, maxTime);

  const segments = useGetCycleSegments(pings);
  const noSignalBlocks = useMemo(
    () => getNoSignalBlocks(segments, firstPingTime, lastPingTime),
    [segments, firstPingTime, lastPingTime],
  );

  const { graphRef, indicatorValues } = useIndicatorValues(
    minTime,
    maxTime,
    noSignalBlocks,
  );

  const maxSpeed = useMemo(
    () => pings.reduce((max, p) => Math.max(max, Number(p.speed) || 0), 0),
    [pings],
  );

  const yAxisValues = useMemo(() => {
    const values: number[] = [];
    for (let i = 0; i < Y_AXIS_LABELS; i++) {
      values.push(maxSpeed * (1 - i / (Y_AXIS_LABELS - 1)));
    }
    return values;
  }, [maxSpeed]);

  const speedLabel = currentPing?.speed
    ? formatSpeed(parseFloat(currentPing.speed), !!isMetric)
    : null;

  // SVG viewBox width — use a fixed coordinate space, the SVG scales to fill
  const SVG_WIDTH = 1000;

  const { strokePath, fillPath } = useMemo(
    () => buildSpeedPath(pings, startMs, durationMs, maxSpeed, SVG_WIDTH, GRAPH_HEIGHT),
    [pings, startMs, durationMs, maxSpeed],
  );

  return (
    <>
      <TimelineTimeDisplay
        indicatorTime={indicatorValues.indicatorTime}
        label={t('live_map.timeline.time')}
      >
        {speedLabel && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              {t('live_map.timeline.speed')}
            </Typography>
            <Typography variant="body2">{speedLabel}</Typography>
          </Box>
        )}
      </TimelineTimeDisplay>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `${LABEL_WIDTH}px 1fr`,
          columnGap: 1,
        }}
      >
        {/* y-axis labels */}
        <Box
          sx={{
            gridColumn: 1,
            gridRow: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: GRAPH_HEIGHT,
          }}
        >
          {yAxisValues.map((val, i) => (
            <Typography
              key={i}
              variant="body2"
              sx={{
                fontSize: '0.6875rem',
                textAlign: 'right',
                lineHeight: 1,
              }}
            >
              {formatSpeedRounded(val, !!isMetric)}
            </Typography>
          ))}
        </Box>

        <Box sx={{ gridColumn: 2, gridRow: '1 / 3' }}>
          <Box
            ref={graphRef}
            sx={{
              position: 'relative',
              height: GRAPH_HEIGHT,
              border: `1px solid ${theme.brandV2.colors.treadGray6}`,
              borderRadius: theme.brandV2.borderRadius,
              backgroundColor: theme.semantic.surfaceRaised,
              overflow: 'hidden',
            }}
          >
            <TimeIndicator
              visible={indicatorValues.indicatorVisible}
              position={indicatorValues.indicatorPosition}
              time={indicatorValues.indicatorTime}
            />

            <TimelineTickLines startMs={startMs} endMs={endMs} normalize={normalize} />

            {/* horizontal grid lines */}
            {yAxisValues.map((_, i) => {
              if (i === 0) return null;
              const top = (i / (Y_AXIS_LABELS - 1)) * 100;

              return (
                <Box
                  key={`hgrid-${i}`}
                  sx={{
                    position: 'absolute',
                    top: `${top}%`,
                    width: '100%',
                    height: '1px',
                    backgroundColor: theme.brandV2.colors.treadGray6,
                    opacity: 0.25,
                  }}
                />
              );
            })}

            {/* no-signal blocks */}
            {noSignalBlocks.map(([start, end]) => (
              <Box
                key={`nosignal_${start.valueOf()}`}
                sx={{
                  position: 'absolute',
                  left: normalize(start.toISOString()),
                  right: normalize(end.toISOString(), true),
                  height: '100%',
                  bottom: 0,
                  backgroundColor: theme.brandV2.colors.treadGray10,
                }}
              />
            ))}

            {/* speed curve */}
            {strokePath && (
              <svg
                viewBox={`0 0 ${SVG_WIDTH} ${GRAPH_HEIGHT}`}
                preserveAspectRatio="none"
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                }}
              >
                <path d={fillPath} fill="#78B2FF" opacity={0.3} />
                <path
                  d={strokePath}
                  fill="none"
                  stroke="#78B2FF"
                  strokeWidth={2}
                  vectorEffect="non-scaling-stroke"
                />
              </svg>
            )}
          </Box>

          <TimelineTickLabels startMs={startMs} endMs={endMs} normalize={normalize} />
        </Box>
      </Box>
    </>
  );
};
